import { useState, useRef, useEffect, ReactNode } from 'react';
import { Send, RotateCcw, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useRubyPublicChat, RubyPublicContext } from '@/hooks/useRubyPublicChat';
import { RubyAvatar } from '@/components/shared/RubyAvatar';
import { RubyMarkdown } from '@/components/shared/RubyMarkdown';
import { RubyCardType } from '@/components/ruby/RubyPreviewCards';

export interface RubyPublicAssistantApi {
  open: () => void;
  close: () => void;
}

interface RubyPublicAssistantProps {
  context: RubyPublicContext;
  children: (api: RubyPublicAssistantApi) => ReactNode;
}

export function RubyPublicAssistant({ context, children }: RubyPublicAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handleCardClick = (cardType: RubyCardType, action: string) => {
    setIsOpen(false);
    if (action.startsWith('http')) {
      window.open(action, '_blank', 'noopener,noreferrer');
    } else if (action.startsWith('/signup')) {
      window.location.href = action;
    } else {
      navigate(action);
    }
  };

  const {
    messages,
    isLoading,
    quickReplies,
    error,
    sendMessage,
    startConversation,
    selectQuickReply,
    resetConversation,
    hasStarted,
  } = useRubyPublicChat({ context });

  useEffect(() => {
    if (isOpen && !isLoading && messages.length === 0 && !hasStarted) {
      startConversation();
    }
  }, [isOpen, messages.length, hasStarted, isLoading, startConversation]);

  useEffect(() => {
    if (messages.length > 0) {
      lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [messages]);

  useEffect(() => {
    if (context.type === 'auth' && context.signupStep) {
      resetConversation();
    }
  }, [context.signupStep, context.type, resetConversation]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      sendMessage(input.trim());
      setInput('');
    }
  };

  const handleQuickReply = (reply: string) => {
    selectQuickReply(reply);
  };

  const handleReset = () => {
    resetConversation();
    setTimeout(() => startConversation(), 50);
  };

  const getSubtitle = () => {
    if (context.type === 'auth' && context.signupStep) {
      const stepLabels: Record<string, string> = {
        welcome: 'Getting started',
        products: 'Selecting interests',
        email: 'Account setup',
        loading: 'Scanning data',
        reveal: 'Your results',
        password: 'Almost done!',
        creating: 'Creating account',
        complete: 'Welcome!',
      };
      return stepLabels[context.signupStep] || 'Your personal assistant';
    }
    return 'Your personal assistant';
  };

  const api: RubyPublicAssistantApi = {
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  };

  return (
    <>
      {children(api)}

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side="right"
          className="sm:max-w-md flex flex-col p-0"
        >
          {/* Header actions own the close control so it never overlaps Start over
              (SheetContent's absolute X sits at right-4 top-4 over the action row). */}
          <SheetHeader className="p-4 border-b border-border/50 shrink-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-3">
                <RubyAvatar size="lg" className="w-12 h-12 shrink-0" />
                <div className="min-w-0">
                  <SheetTitle className="text-lg font-medium">Ruby</SheetTitle>
                  <p className="text-xs text-muted-foreground">{getSubtitle()}</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  className="h-8 gap-1 px-2 text-muted-foreground hover:text-foreground"
                  aria-label="Start over"
                  title="Start over"
                >
                  <RotateCcw className="w-4 h-4" />
                  {/* Label hidden on narrow sheets so Close stays clear of Start over */}
                  <span className="hidden sm:inline">Start over</span>
                </Button>
                <SheetClose asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 text-muted-foreground hover:text-foreground"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </SheetClose>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && isLoading && (
              <div className="flex justify-start">
                <div className="bg-muted/50 rounded-2xl px-4 py-3">
                  <div className="flex gap-1">
                    <span
                      className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce"
                      style={{ animationDelay: '0ms' }}
                    />
                    <span
                      className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce"
                      style={{ animationDelay: '150ms' }}
                    />
                    <span
                      className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce"
                      style={{ animationDelay: '300ms' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {messages.map((message, index) => (
              <div
                key={message.id}
                ref={index === messages.length - 1 ? lastMessageRef : null}
                className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[85%] rounded-2xl px-4 py-3',
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-foreground',
                  )}
                >
                  <RubyMarkdown
                    content={message.content}
                    className="text-base"
                    onCardClick={handleCardClick}
                  />
                </div>
              </div>
            ))}

            {isLoading && messages.length > 0 && (
              <div className="flex justify-start">
                <div className="bg-muted/50 rounded-2xl px-4 py-3">
                  <div className="flex gap-1">
                    <span
                      className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce"
                      style={{ animationDelay: '0ms' }}
                    />
                    <span
                      className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce"
                      style={{ animationDelay: '150ms' }}
                    />
                    <span
                      className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce"
                      style={{ animationDelay: '300ms' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="flex justify-center">
                <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">
                  {error}
                </p>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {quickReplies.length > 0 && !isLoading && (
            <div className="px-4 pb-2 shrink-0">
              <div className="space-y-2">
                {quickReplies.map((reply, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleQuickReply(reply)}
                    className={cn(
                      'w-full text-left px-4 py-3 rounded-xl',
                      'border-2 border-border/50 hover:border-primary/50',
                      'bg-card hover:bg-muted/30',
                      'transition-all duration-200',
                      'flex items-center gap-3',
                    )}
                  >
                    <div className="w-4 h-4 rounded-full border-2 border-primary/50 shrink-0" />
                    <span className="text-sm font-medium text-foreground">{reply}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-4 border-t border-border/50 shrink-0">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                disabled={isLoading}
                className={cn(
                  'flex-1 px-4 py-3 rounded-xl',
                  'bg-muted/30 border-2 border-border/50',
                  'focus:border-primary/50 focus:outline-none',
                  'text-sm placeholder:text-muted-foreground',
                  'transition-colors',
                )}
              />
              <Button
                type="submit"
                size="icon"
                disabled={!input.trim() || isLoading}
                className="h-12 w-12 rounded-xl"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
